import { doc, runTransaction } from 'firebase/firestore';
import { createRecordId } from './journal-metrics';

export const MAX_ARCHIVE_SELECTION = 400;
export const isArchived = trade => typeof trade.archiveFolderId === 'string' && trade.archiveFolderId.length > 0;
export const activeJournalTrades = trades => trades.filter(t => !t.deleted && !isArchived(t));
export const archiveRef = (db, uid) => doc(db, 'users', uid, 'meta', 'tradeArchives');
const folderName = value => {
  const name = String(value || '').trim().replace(/\s+/g, ' ');
  if (!name || name.length > 60) throw new Error('Give this folder a name of 1–60 characters.');
  return name;
};
export function readFolders(snapshot) {
  const value = snapshot.exists() ? snapshot.data().folders : {};
  return value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).filter(([, folder]) => folder && typeof folder.name === 'string' && folder.name.trim())) : {};
}
function assertUnique(folders, name, except) {
  if (Object.entries(folders).some(([id, folder]) => id !== except && folder.name?.toLowerCase() === name.toLowerCase())) {
    throw new Error('That folder already exists. Choose it from the list or use another name.');
  }
}
export async function saveArchiveFolder(db, uid, name, id = createRecordId()) {
  if (!uid) throw new Error('Sign in again to save this folder.');
  name = folderName(name);
  const ref = archiveRef(db, uid);
  await runTransaction(db, async tx => {
    const folders = readFolders(await tx.get(ref));
    assertUnique(folders, name, id);
    tx.set(ref, { folders: { ...folders, [id]: { ...folders[id], name, createdAt: folders[id]?.createdAt || Date.now() } } });
  });
  return id;
}

// One transaction keeps a selection together and reads the latest documents:
// moving a trade never overwrites notes, images, or a concurrent deletion.
export async function moveArchiveTrades(db, uid, ids, { folderId = '', newFolderName = '' } = {}) {
  const unique = [...new Set(ids)];
  if (!uid || !unique.length) throw new Error('Select at least one trade.');
  if (unique.length > MAX_ARCHIVE_SELECTION) throw new Error(`Move up to ${MAX_ARCHIVE_SELECTION} trades at a time.`);
  const newName = newFolderName ? folderName(newFolderName) : '';
  const destination = newName ? createRecordId() : folderId;
  const meta = archiveRef(db, uid);
  const refs = unique.map(id => doc(db, 'users', uid, 'trades', id));
  await runTransaction(db, async tx => {
    const [snapshot, ...records] = await Promise.all([tx.get(meta), ...refs.map(ref => tx.get(ref))]);
    const folders = readFolders(snapshot);
    if (newName) assertUnique(folders, newName);
    else if (destination && !Object.hasOwn(folders, destination)) throw new Error('This folder is no longer available. Choose another folder.');
    if (records.some(record => !record.exists() || record.data().deleted)) throw new Error('A selected trade changed or was deleted. Refresh your selection and try again.');
    const now = Date.now();
    if (newName) tx.set(meta, { folders: { ...folders, [destination]: { name: newName, createdAt: now } } });
    records.forEach((record, i) => tx.update(refs[i], {
      archiveFolderId: destination || null,
      archivedAt: destination ? record.data().archivedAt || now : null,
      deleted: false,
      // Restoring a trade never automatically republishes it.
      isPublic: false,
    }));
  });
  return { count: unique.length, folderId: destination };
}

export async function setTradeVisibility(db, uid, id, isPublic) {
  const target = doc(db, 'users', uid, 'trades', id);
  await runTransaction(db, async tx => {
    const snapshot = await tx.get(target);
    if (!snapshot.exists() || snapshot.data().deleted) throw new Error('This trade is no longer available. Refresh your journal.');
    if (isPublic && isArchived(snapshot.data())) throw new Error('Restore this trade to your journal before making it public.');
    tx.update(target, { isPublic: !!isPublic });
  });
}

export async function updateSavedTrade(db, uid, trade) {
  const target = doc(db, 'users', uid, 'trades', trade.id);
  return runTransaction(db, async tx => {
    const snapshot = await tx.get(target);
    if (!snapshot.exists() || snapshot.data().deleted) throw new Error('This trade is no longer available. Refresh your journal.');
    const current = snapshot.data();
    // An editor opened in another tab must not undo a later archive or restore.
    const saved = { ...trade, archiveFolderId: current.archiveFolderId || null, archivedAt: current.archivedAt || null,
      isPublic: isArchived(current) ? false : !!current.isPublic, deleted: false };
    tx.set(target, saved);
    return saved;
  });
}
